import axios from 'axios';
import { locationHistoryService } from '../services/locationHistoryService';
import { parseApiError } from '../utils/apiError';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Unauthenticated client for open/public endpoints (checkout, session management, etc.)
export const publicClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor for tokens
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Refresh token state ──────────────────────────────────────────────────

let isRefreshing = false;
let pendingQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

export const onTokenRefreshedCallbacks: Array<(access: string, refresh?: string) => void> = [];

function processQueue(error: unknown, token: string | null) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });
  pendingQueue = [];
}

function clearSession() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_data');
  try {
    localStorage.removeItem('user-storage');
  } catch {
    // ignore
  }
  locationHistoryService.clearHistory();
  if (window.location.pathname !== '/sign-in') {
    const returnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.href = `/sign-in?redirect=${encodeURIComponent(returnUrl)}`;
  }
}

const REFRESH_URL = '/custom_account/api/token/refresh/';

// Response interceptor with transparent refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 401 = session/auth failure → try a token refresh. 403 (PERMISSION_DENIED) is an
    // access problem for a valid session and must reach the UI untouched.
    const shouldAttemptRefresh =
      parseApiError(error).status === 401 &&
      !originalRequest?._isRetry &&
      originalRequest?.url !== REFRESH_URL &&
      originalRequest?.headers?.Authorization?.startsWith('Bearer ');

    if (!shouldAttemptRefresh) {
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      clearSession();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      }).then((newToken) => {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        originalRequest._isRetry = true;
        return apiClient(originalRequest);
      });
    }

    isRefreshing = true;
    originalRequest._isRetry = true;

    try {
      const { data } = await publicClient.post(REFRESH_URL, {
        refresh: refreshToken,
      });

      if (!data?.access) {
        processQueue(new Error('Invalid refresh response'), null);
        clearSession();
        return Promise.reject(error);
      }

      localStorage.setItem('access_token', data.access);
      if (data.refresh) {
        localStorage.setItem('refresh_token', data.refresh);
      }

      onTokenRefreshedCallbacks.forEach((cb) => cb(data.access, data.refresh));

      processQueue(null, data.access);

      originalRequest.headers.Authorization = `Bearer ${data.access}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      const status = parseApiError(refreshError).status;

      // Only clear session if the refresh token itself was rejected
      if (status === 401) {
        processQueue(refreshError, null);
        clearSession();
        return Promise.reject(error);
      }

      // Network / server error — keep tokens, surface original error
      processQueue(refreshError, null);
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  },
);
